import { ModalAction } from '@overlayqa/shared/ui';
/**
 * ProjectSharingSection — Owner-only panel to manage project visibility and
 * per-user share grants.
 *
 * Shows:
 * - A "Visible to entire team" toggle (sets visibility to 'team' or 'private')
 * - Current shares list (avatar, name, role dropdown, remove button)
 * - Add member form (team member select + role)
 *
 * Only rendered when the calling user's project role is 'owner'.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { UserGroupIcon } from '@hugeicons/core-free-icons';
import { ToggleRow } from '../ui/ToggleRow';
import { toast } from 'sonner';
import { Avatar } from '../ui/Avatar';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useApiMutation } from '../../hooks/useApiMutation';
import { projectSharesApi, type ProjectShare } from '../../lib/api/projectShares';
import { teamsApi, type TeamMemberSummary } from '../../lib/api/teams';
import { SelectDropdown } from '../shared/SelectDropdown';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { LoadError } from '../shared/IssuesLoadError';

/** Project access uses the dashboard picker for forms and inline edits. */
const SHARE_ROLE_OPTIONS: { value: 'viewer' | 'collaborator'; label: string }[] = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'collaborator', label: 'Collaborator' },
];
import { log } from '@/lib/logger';

log.info('[ProjectSharingSection] Module loaded');

interface ProjectSharingSectionProps {
  projectId: string;
  visibility: 'private' | 'team';
  teamId: string;
  /** Internal DB id of the signed-in caller (the owner). Used to keep them out
      of the "Add a member" dropdown — sharing with yourself is a server 400. */
  currentUserId?: string;
  /** True while currentUserId (from /users/me) is still resolving. Until it
      resolves, currentUserId is undefined and the exclusion filter is
      vacuously true for every member — so the dropdown renders nothing
      rather than a stale unfiltered list that can offer the owner. */
  currentUserLoading?: boolean;
  onVisibilityChange: (visibility: 'private' | 'team') => void;
  /** True while the visibility PATCH is in flight. The Toggle is fully
      controlled by project.visibility (no optimistic update), so without
      this it stays clickable and repeat toggles queue conflicting PATCHes. */
  visibilityUpdating?: boolean;
  /** Rendered under Team visibility (the "Client reviews" defaults). */
  children?: ReactNode;
}

export function ProjectSharingSection({
  projectId,
  visibility,
  teamId,
  currentUserId,
  currentUserLoading = false,
  onVisibilityChange,
  visibilityUpdating = false,
  children,
}: ProjectSharingSectionProps) {
  log.info('[ProjectSharingSection] Render:', { projectId, visibility, teamId, visibilityUpdating });

  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<'viewer' | 'collaborator'>('viewer');
  const [addError, setAddError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ shareId: string; userName: string } | null>(null);

  // Fetch current shares for this project
  const sharesQuery = useApiQuery<ProjectShare[]>({
    queryKey: ['projects', projectId, 'shares'],
    queryFn: (token) => projectSharesApi.list(token, projectId),
    enabled: !!projectId,
  });

  // Fetch team members so we can populate the "Add member" dropdown
  const membersQuery = useApiQuery<TeamMemberSummary[]>({
    queryKey: ['teams', teamId, 'members'],
    queryFn: (token) => teamsApi.listMembers(token, teamId),
    enabled: !!teamId,
  });

  const createShareMutation = useApiMutation<ProjectShare, { userId: string; role: 'viewer' | 'collaborator' }>({
    mutationFn: (token, { userId, role }) => projectSharesApi.create(token, projectId, userId, role),
    invalidateKeys: [['projects', projectId, 'shares']],
  });

  const updateShareMutation = useApiMutation<ProjectShare, { shareId: string; role: 'viewer' | 'collaborator' }>({
    mutationFn: (token, { shareId, role }) => projectSharesApi.update(token, projectId, shareId, role),
    invalidateKeys: [['projects', projectId, 'shares']],
  });

  const removeShareMutation = useApiMutation<{ deleted: boolean }, { shareId: string }>({
    mutationFn: (token, { shareId }) => projectSharesApi.remove(token, projectId, shareId),
    invalidateKeys: [['projects', projectId, 'shares']],
  });

  const shares = sharesQuery.data ?? [];
  const members = membersQuery.data ?? [];

  // Members who are not already shared with and not the owner themselves
  const sharedUserIds = new Set(shares.map((s) => s.userId));
  const availableMembers = members.filter(
    (m) => m.user && !sharedUserIds.has(m.user.id) && m.user.id !== currentUserId
  );

  // While currentUserId is still resolving, the exclusion above is vacuously
  // true for every member (undefined never equals a real id), so the form
  // stays hidden rather than briefly offering the owner as a share target.
  const addMemberLoading = membersQuery.isLoading || currentUserLoading;

  const handleToggleVisibility = () => {
    const newVisibility = visibility === 'team' ? 'private' : 'team';
    log.info('[ProjectSharingSection] Toggling visibility', { from: visibility, to: newVisibility });
    onVisibilityChange(newVisibility);
  };

  const handleAddMember = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAddError(null);
    setSuccessMessage(null);

    if (!addUserId) {
      setAddError('Select a team member to add.');
      return;
    }

    log.info('[ProjectSharingSection] Adding share:', { userId: addUserId, role: addRole });
    createShareMutation.mutate(
      { userId: addUserId, role: addRole },
      {
        onSuccess: (share) => {
          log.info('[ProjectSharingSection] Share created:', { id: share.id });
          setAddUserId('');
          setAddRole('viewer');
          setSuccessMessage(`${share.user?.name || 'Member'} added as ${share.role}.`);
        },
        onError: (err) => {
          log.info('[ProjectSharingSection] Failed to add share:', { message: err.message });
          setAddError(err.message || 'Failed to add member.');
        },
      }
    );
  };

  const handleRoleChange = (shareId: string, role: 'viewer' | 'collaborator') => {
    log.info('[ProjectSharingSection] Updating share role:', { shareId, role });
    updateShareMutation.mutate(
      { shareId, role },
      {
        onError: (err) =>
          toast.error(err instanceof Error && err.message ? err.message : 'Failed to update role. Please try again.'),
      },
    );
  };

  const handleRemove = (shareId: string, userName: string) => {
    setConfirmRemove({ shareId, userName });
  };

  const confirmRemoveShare = () => {
    if (!confirmRemove) return;
    log.info('[ProjectSharingSection] Removing share:', { shareId: confirmRemove.shareId });
    removeShareMutation.mutate(
      { shareId: confirmRemove.shareId },
      {
        onSettled: () => setConfirmRemove(null),
        onError: (err) =>
          toast.error(err instanceof Error && err.message ? err.message : 'Failed to remove access. Please try again.'),
      },
    );
  };

  return (
    <>
    <div className="space-y-6">
      {/* Visibility toggle */}
      <div>
        <h3 className="section-label mb-3">Team visibility</h3>
        <ToggleRow
          icon={UserGroupIcon}
          label="Visible to entire team"
          hint={visibility === 'team'
            ? 'All team members can view this project and its issues.'
            : 'Only you and people you share it with can access this project.'}
          checked={visibility === 'team'}
          onChange={handleToggleVisibility}
          disabled={visibilityUpdating}
        />
      </div>

      {children}

      {/* Shares list */}
      <div>
        <h3 className="section-label mb-3">Shared with</h3>
        {sharesQuery.isLoading ? (
          <p className="state-note">Loading...</p>
        ) : sharesQuery.error ? (
          <LoadError
            compact
            resource="shares"
            error={sharesQuery.error as Error}
            onRetry={() => sharesQuery.refetch()}
          />
        ) : shares.length === 0 ? (
          <p className="state-note">Not shared with anyone yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden">
            {shares.map((share) => (
              <li key={share.id} className="flex items-center gap-3 px-4 py-3 bg-white">
                {/* Avatar */}
                <Avatar id={share.user?.id} src={share.user?.avatarUrl} name={share.user?.name || share.user?.email} />

                {/* Name + email */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {share.user?.name || share.user?.email || 'Unknown'}
                  </p>
                  {share.user?.email && share.user.name && (
                    <p className="field-hint truncate">{share.user.email}</p>
                  )}
                </div>

                {/* Role selector. An inline record edit: it saves on change and
                    sits outside the add-member form, so it is a SelectDropdown.
                    It was a native <select> until 2026-09-04 for one mechanical
                    reason (it must go inert mid-mutation and SelectDropdown had
                    no `disabled`), which made it the only one of 27 call sites
                    breaking the rule. The sr-only label became `aria-label` with
                    the same words: the trigger is a button, not a form control. */}
                <SelectDropdown
                  value={share.role}
                  options={SHARE_ROLE_OPTIONS}
                  onChange={(v) => handleRoleChange(share.id, v as 'viewer' | 'collaborator')}
                  disabled={updateShareMutation.isPending}
                  aria-label={`Role for ${share.user?.name || 'member'}`}
                />

                {/* Remove button */}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => handleRemove(share.id, share.user?.name || 'member')}
                  disabled={removeShareMutation.isPending}
                  aria-label={`Remove ${share.user?.name || 'member'} from project`}
                ><ModalAction kind="unlink">
                  Remove
                </ModalAction></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add member form */}
      <div>
        <h3 className="section-label mb-3">Add a member</h3>
        {addMemberLoading ? (
          <p className="state-note">Loading...</p>
        ) : availableMembers.length === 0 ? (
          <p className="state-note">All team members already have access.</p>
        ) : (
          <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={handleAddMember}>
            <div className="flex-1">
              <label htmlFor="share-add-user" className="sr-only">Team member</label>
              <SelectDropdown
                id="share-add-user"
                className="w-full"
                value={addUserId}
                onChange={(value) => setAddUserId(value)}
                disabled={createShareMutation.isPending || membersQuery.isLoading}
                options={[{ value: '', label: 'Select a team member…' }, ...availableMembers.map((member) => ({ value: member.user!.id, label: member.user!.name || member.user!.email }))]}
              />
            </div>
            <div>
              <label htmlFor="share-add-role" className="sr-only">Role</label>
              <SelectDropdown
                id="share-add-role"
                value={addRole}
                onChange={(value) => setAddRole(value as 'viewer' | 'collaborator')}
                disabled={createShareMutation.isPending}
                options={SHARE_ROLE_OPTIONS}
              />
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={createShareMutation.isPending || !addUserId}
            ><ModalAction kind="invite">
              {createShareMutation.isPending ? 'Adding...' : 'Add'}
            </ModalAction></button>
          </form>
        )}

        {addError && (
          <p className="mt-2 text-xs text-red-600" role="alert">{addError}</p>
        )}
        {successMessage && (
          <p className="mt-2 text-xs text-green-700" role="status">{successMessage}</p>
        )}

        <p className="field-hint mt-2">
          <strong>Viewer</strong> can view issues but cannot create or edit.
          &nbsp;<strong>Collaborator</strong> can create and edit issues.
        </p>
      </div>
    </div>

    <ConfirmDialog
      open={confirmRemove !== null}
      title="Remove access"
      message={`Remove ${confirmRemove?.userName ?? 'this member'}'s access to this project?`}
      confirmLabel="Remove"
      onConfirm={confirmRemoveShare}
      onCancel={() => setConfirmRemove(null)}
      destructive
      loading={removeShareMutation.isPending}
    />
    </>
  );
}
